import * as React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  List,
  Popover,
  SelectChangeEvent,
  Typography,
  styled,
  CircularProgress,
  Button,
  FormControl,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { AppBody } from "../../AppBody";
import Breadcrumbs from "../../../containers/Breadcrumbs";
import {
  SODCustomfileNameFormat,
  appHeaderTitle,
  getCookiebyName,
  sodVariableDefinitionData,
  sortObjValue,
  yearChoice,
} from "../../../common/services/Utilities";
import HelpButton from "../../HelpContent/HelpButton";
import BackButton from "../../../containers/BackButton";
import CloudFoundryBanner from "../../../containers/CloudFoundryBanner";
import { useLocation, useHistory, Link as RouterLink } from "react-router-dom";
import { AppState, KeyValue, KeyValueObject, Query } from "../../../interfaces";
import SelectFilterField from "../../../containers/SelectFilterField";
import {
  selectedStatesAlpha,
  stateNameToAbbrv,
} from "../../../refdata/FilterOptions";
import useLastYearFilter from "../../../hooks/useLastYearFilter";
import makeStyles from "@mui/styles/makeStyles/makeStyles";
import { TreeItem, TreeItemProps, TreeView } from "@mui/x-tree-view";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ApiBaseUrl, AppUrl } from "../../../config";
import { apiPost, fetchData } from "../../../common/services/ApiClient";
import { CSVLink } from "react-csv";
import { transformStateName } from "../SummaryTables/SummaryTableUtilities";
import Edit from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import LookupVariable from "../../../common/components/LookupVariable";
import SaveNameInput from "../../PeerGroup/SaveNameInput";
import ModalDialog from "../../ModalDialog";

interface Props {
  appState: Query;
  uuid: AppState;
  updateFilter: (filterField: string, value: string | number) => void;
}
interface CustomTreeItemProps extends TreeItemProps {
  labelText: string;
  variable: string;
  checked: boolean;
  onCheck: (event: React.ChangeEvent<HTMLInputElement>) => void;
  expanded: string;
  onToggle: () => void;
  indeterminate: boolean;
  definition: string;
  disabled?: boolean;
  hasbackground?: string;
}
interface TreeNode {
  id: string;
  title: string;
  variable?: string;
  children?: TreeNode[];
  definition?: string;
  SODType?: string;
}

const useStyles = makeStyles(theme => ({
  searchPanelHeadings: {
    color: "#fff",
    fontWeight: "bold",
    padding: 0,
  },
  list: {
    padding: 0,
  },
  dialogActions: {
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "marginTop": "1rem",
    "height": "85px",
    "backgroundColor": theme.palette.primary.main,
    "& button.MuiButton-containedSecondary": {
      fontSize: "1rem",
      margin: "1rem",
    },
    "& button.MuiButton-contained.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },
  fieldErrors: {
    "wordBreak": "break-word",
    "padding": ".25rem",
    "margin": ".25rem 0",
    "borderRadius": 5,
    "& .fieldName p": {
      padding: ".5rem",
    },
    "&:hover": {
      backgroundColor: "#D9E8F6",
    },
    "& .modifyButton": {
      "padding": ".5rem",
      "float": "right",
      "&:hover": { backgroundColor: "#ffffff" },
    },
    "&:last-child": {
      marginBottom: "1rem",
    },
  },
  manuallyEditButtons: {
    "& .MuiButton-root.MuiButton-containedSecondary": {
      margin: 0,
    },
  },
  textArea: {
    "marginBottom": "1rem",
    "& textarea": {
      textTransform: "uppercase",
    },
  },
  variableErrors: {
    "& h3": { padding: 0 },
  },
  editField: {
    "& input": {
      textTransform: "uppercase",
    },
    "padding": ".25rem",
    "margin": ".25rem 0",
  },
}));
const StyledTreeItem = styled(
  (props: TreeItemProps & { hasbackground?: string }) => (
    <TreeItem {...props} />
  ),
)(({ hasbackground }) => ({
  "& .MuiTreeItem-content": {
    "flexDirection": "row-reverse",
    "marginBottom": "8px",
    "position": "relative",
    "color": "#1A4480",
    "backgroundColor": hasbackground === "true" ? "#D9E8F6" : "transparent",
    "&:hover": {
      backgroundColor: hasbackground === "true" ? "#D5E5F6" : "#f2f2f2",
    },
    "&:focused": {
      backgroundColor: hasbackground === "true" ? "#D5E5F6" : "transparent",
    },
  },
  "& .Mui-selected ": {
    backgroundColor: "transparent",
  },
  "& .MuiTreeItem-label": {
    display: "flex",
    alignItems: "center",
  },
  "& .MuiFormControlLabel-label": {
    color: "#1A4480",
  },
  "& li.MuiTreeItem-root": {
    backgroundColor: "white",
  },
}));
const CustomTreeItem: React.FC<CustomTreeItemProps> = props => {
  const {
    id,
    labelText,
    variable,
    indeterminate,
    checked,
    onCheck,
    definition,
    disabled,
    ...other
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  return (
    <StyledTreeItem
      onClick={event => {
        event.stopPropagation();
      }}
      label={
        <Grid container>
          <Grid item sm={10}>
            <FormControlLabel
              onClick={event => {
                event.stopPropagation();
              }}
              control={
                <Checkbox
                  id={`${id}_checkbox`}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={event => {
                    onCheck(event);
                  }}
                  disabled={disabled}
                />
              }
              label={labelText}
            />
          </Grid>
          <Grid item sm={2} id={`${id}_expand`}>
            <Typography
              aria-owns={open ? "mouse-over-popover" : undefined}
              aria-haspopup="true"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              variant="body2"
              style={{
                textDecoration: "underline",
                color: "#0000ff",
                float: "right",
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              {variable}
            </Typography>

            <Popover
              id="mouse-over-popover"
              sx={{
                pointerEvents: "none",
              }}
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{ paper: { style: { maxWidth: "750px" } } }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <Typography style={{ padding: "1rem 1rem 0" }} variant="h3">
                {variable}
              </Typography>
              <Typography style={{ padding: "0 1rem 1rem" }} variant="body1">
                {definition}
              </Typography>
            </Popover>
          </Grid>
        </Grid>
      }
      {...other}
    />
  );
};

const data: TreeNode[] = sodVariableDefinitionData;
const getAllNodeIds = (node: TreeNode): string[] => {
  let ids = [node.id];
  if (node.children) {
    for (const child of node.children) {
      ids = ids.concat(getAllNodeIds(child));
    }
  }
  return ids;
};

const getNodeState = (
  node: TreeNode,
  checkedNodes: Set<string>,
): { checked: boolean; indeterminate: boolean } => {
  if (!node.children) {
    return { checked: checkedNodes.has(node.id), indeterminate: false };
  }

  const childrenState = node.children.map(child =>
    getNodeState(child, checkedNodes),
  );
  const allChecked = childrenState.every(state => state.checked);
  const someChecked = childrenState.some(
    state => state.checked || state.indeterminate,
  );

  return {
    checked: allChecked,
    indeterminate: someChecked && !allChecked,
  };
};
const getCheckedVariables = (
  node: TreeNode,
  checkedNodes: Set<string>,
): string[] => {
  let variables: string[] = [];

  if (checkedNodes.has(node.id) && node.variable) {
    variables.push(node.variable);
  }

  if (node.children) {
    for (const child of node.children) {
      variables = variables.concat(getCheckedVariables(child, checkedNodes));
    }
  }

  return variables;
};
const renderTree = (
  nodes: TreeNode,
  checkedNodes: Set<string>,
  expandedNodes: Set<string>,
  handleToggle: (nodeId: string, checked: boolean) => void,
  handleExpand: (nodeId: string) => void,
  isDisabled: boolean = false,
) => {
  const { checked, indeterminate } = getNodeState(nodes, checkedNodes);
  return (
    <CustomTreeItem
      key={nodes.id}
      id={nodes.id}
      nodeId={nodes.id}
      labelText={nodes.title}
      variable={nodes.variable ? nodes.variable : ""}
      checked={checked}
      indeterminate={indeterminate}
      onCheck={event => handleToggle(nodes.id, event.target.checked)}
      expanded={expandedNodes.has(nodes.id) ? "true" : "false"}
      onToggle={() => handleExpand(nodes.id)}
      definition={nodes.definition ? nodes.definition : ""}
      disabled={nodes.id === "mandatory"}
      hasbackground={
        nodes.id === "mandatory" ||
        nodes.id === "branch" ||
        nodes.id === "BHC" ||
        nodes.id === "institution"
          ? "true"
          : "false"
      }
    >
      {Array.isArray(nodes.children)
        ? nodes.children.map(node =>
            renderTree(
              node,
              checkedNodes,
              expandedNodes,
              handleToggle,
              handleExpand,
              isDisabled,
            ),
          )
        : null}
    </CustomTreeItem>
  );
};

export default function CustomDataDownload({
  appState,
  uuid,
  updateFilter,
}: Props) {
  const classes = useStyles();
  const anchorRef = React.useRef(null);
  const history = useHistory();

  const [loading, setLoading] = React.useState(false);
  const { lastYear } = useLastYearFilter(appState.filters, updateFilter);
  const [customForm, setCustomForm] = React.useState({
    lastYear: appState.filters.lastYear,
    stname: "ALL",
  });

  const [customDownloadData, setCustomDownloadData] = React.useState<
    KeyValue[]
  >([]);
  const mandatoryVariables = React.useMemo(
    () => [
      "YEAR",
      "CERT",
      "NAMEFULL",
      "ADDRESBR",
      "BRNUM",
      "UNINUMBR",
      "STALPBR",
    ],
    [],
  );
  const [checkedVariables, setCheckedVariables] = React.useState<string[]>([
    "YEAR",
    "CERT",
    "NAMEFULL",
    "ADDRESBR",
    "BRNUM",
    "UNINUMBR",
    "STALPBR",
  ]);
  const [editError, setEditError] = React.useState("");
  const [editMany, setEditMany] = React.useState(false);
  const [errorFields, setErrorFields] = React.useState<string[]>([]);
  const [tempNewErrorState, setTempNewErrorState] = React.useState<string[]>(
    errorFields,
  );
  const [selectedValues, setSelectedValues] = React.useState("");

  const [selectedVariables, setSelectedVariables] = React.useState<string[]>([
    "YEAR",
    "CERT",
    "NAMEFULL",
    "ADDRESBR",
    "BRNUM",
    "UNINUMBR",
  ]);
  const [selectedConfig, setSelectedConfig] = React.useState<CustomConfig>();

  const arraysAreEqual = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }

    return arr1.every((value, index) => value === arr2[index]);
  };
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>(
    selectedStatesAlpha.map(state => state.value),
  );

  const [
    selectedStateAndTerritories,
    setSelectedStateAndTerritories,
  ] = React.useState(Object.values(stateNameToAbbrv));
  const handleValuesUpdate = (values: any) => {
    const val = values.split("\n");
    const vals = val.filter((e: string) => {
      return !mandatoryVariables.includes(e.trim());
    });
    setSelectedValues(vals.join(`\n`));
  };
  const vars = sodVariableDefinitionData.flatMap(item =>
    item.children.map(child => child.id),
  );

  const saveEditMany = () => {
    const editManyObj: KeyValueObject[] = [];
    selectedValues.split(`\n`).forEach((variable: string) => {
      editManyObj.push({
        key: variable.toUpperCase().replace(/ /g, ""),
        value: true,
      });
    });
    const uniqueVariables = Array.from(
      new Set(
        selectedValues
          .toUpperCase()
          .replace(/[^a-zA-Z0-9_\s\n]/g, "")
          .trim()
          .split(`\n`)
          .concat(mandatoryVariables)
          .filter(item => item !== "" && vars.includes(item)),
      ),
    );
    setSelectedVariables(uniqueVariables);
    const uniqueErrors = Array.from(
      new Set(
        selectedValues
          .toUpperCase()
          .replace(/[^a-zA-Z0-9_\s\n]/g, "")
          .trim()
          .split(`\n`)
          .concat(mandatoryVariables)
          .filter(item => item !== "" && !vars.includes(item)),
      ),
    );
    setErrorFields(uniqueErrors);
    setEditMany(false);
  };
  const editManyDisplay = (
    <>
      <Typography variant={"h3"} style={{ marginTop: 16 }}>
        Manually Add Variables
      </Typography>
      <Typography variant="body2">
        Enter your variables one per line:
      </Typography>
      <TextField
        fullWidth
        multiline
        id="manuallyAddTextArea"
        className={classes.textArea}
        minRows={15}
        variant="outlined"
        aria-label="manually add textarea"
        value={selectedValues}
        onChange={(e: any) => setSelectedValues(e.target.value)}
      />
      <Typography
        variant="body2"
        style={{
          fontStyle: "italic",
        }}
      >
        The order in which your variables are listed here will be reflected in
        the final output of your download. Reorder to suit your ultimate needs.
      </Typography>
      <Grid container spacing={2} className={classes.manuallyEditButtons}>
        <Grid item md={6} xs={12} style={{ marginTop: 8 }}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={() => {
              setEditMany(false);
            }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item md={6} xs={12} style={{ marginTop: 8 }}>
          <Button
            id="saveManuallyEdit"
            fullWidth
            variant="contained"
            color="secondary"
            type="submit"
            onClick={saveEditMany}
          >
            Save Edits
          </Button>
        </Grid>
        <Grid item xs={12}>
          <LookupVariable
            appState={appState}
            title="Dictionary of SOD Variables"
            text={
              "Use this dictionary to manually select variables by browsing through their definitions. You can also use it to correct or delete any unrecognized variable errors. You must correct all variable errors before you can download your data. For more information, download: "
            }
            appDisplay="4"
            link={
              <Link
                href={`${AppUrl("API")}/SOD_Variables_and_Definitions.csv`}
                download
              >
                SOD Variables and Definitions.csv
              </Link>
            }
            mandatorySelections
            mandatoryVariables={mandatoryVariables.join(", ")}
            onValuesUpdate={handleValuesUpdate}
            selectedVariables={selectedVariables}
          />
        </Grid>
      </Grid>
    </>
  );

  const location = useLocation();
  const [sodState] = React.useState(location);
  const [savedConfigs, setSavedConfigs] = React.useState<CustomConfig[]>([]);
  const [configName, setConfigName] = React.useState<string>("");
  const [createEditState, setCreateEditState] = React.useState(false);
  const [editState, setEditState] = React.useState(false);
  const [editConfig, setEditConfig] = React.useState<CustomConfig>();
  const [tempSelected, setTempSelected] = React.useState<string[]>([
    ...selectedStatesAlpha.map(obj => obj.value),
  ]);

  React.useEffect(() => {
    if (sodState?.history?.includes("peergroup") && !createEditState) {
      setCreateEditState(true);
    }
  }, [createEditState, sodState]);

  React.useEffect(() => {
    if (savedConfigs.length > 0 && createEditState && !editState) {
      if (sodState?.name && sodState.name !== "") {
        setConfigName(sodState.name);
        const savedConfigArray: CustomConfig[] = savedConfigs;
        const matchingConfig: CustomConfig | undefined = savedConfigArray.find(
          (item: KeyValue) => item.label === sodState.name,
        );
        if (matchingConfig) {
          setEditConfig(matchingConfig);
          setTempSelected(matchingConfig.savedState.location);
        }
        setEditState(true);
      }
    }
  }, [createEditState, editState, savedConfigs, sodState]);

  const selectedStates =
    selectedConfig || editConfig
      ? selectedStateAndTerritories
      : selectedOptions;

  const GeographicScopeSelection = () => {
    const handleCheckboxChange = (value: string) => {
      setTempSelected(prev => {
        const flattenedPrev = prev.flat();
        return flattenedPrev.includes(value)
          ? flattenedPrev.filter(item => item !== value)
          : [...flattenedPrev, value];
      });
    };
    const handleSelectAll = () => {
      setTempSelected(prevSelected =>
        prevSelected.length > 0
          ? []
          : selectedStatesAlpha.map(state => state.value),
      );
    };

    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                tempSelected.flat().length === selectedStatesAlpha.flat().length
              }
              indeterminate={
                tempSelected.flat().length > 0 &&
                tempSelected.flat().length !== selectedStatesAlpha.flat().length
              }
              onChange={handleSelectAll}
            />
          }
          label={"Select All / None"}
        />
        <Grid container spacing={0}>
          {selectedStatesAlpha.map((state, index) => (
            <Grid item xs={3} key={index}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tempSelected.flat().includes(state.value)}
                    onChange={() => handleCheckboxChange(state.value)}
                    name={state.label}
                  />
                }
                label={state.label}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  };
  function removeErrorField(error: string) {
    setErrorFields(errorFields.filter((field: string) => field !== error));
    setSelectedVariables(
      selectedVariables.filter((field: string) => field !== error),
    );
  }
  function removeAllErrorFields(errors: string[]) {
    setErrorFields([]);

    setSelectedVariables(
      selectedVariables.filter((value: string) => !errors.includes(value)),
    );
  }
  function editSubmit(currentError: string, newError: string) {
    const errors: string[] = [];
    let newCheckboxState = selectedVariables.map((value: string) =>
      value === currentError ? newError : value,
    );
    const newErrorState = errorFields.map((value: string) =>
      value === currentError ? newError : value,
    );
    if (currentError === newError || !newError) {
      setEditError("");
    } else {
      newErrorState.forEach((field: string) => {
        if (!vars.includes(field)) {
          errors.push(field);
        } else if (!selectedVariables.includes(field)) {
          removeErrorField(field);
          newCheckboxState = [...newCheckboxState, field];
        }
      });
      setErrorFields(errors);
      setTempNewErrorState(errors);
      setSelectedVariables(newCheckboxState);
    }
  }
  const errorFieldDisplay = errorFields.length > 0 && (
    <Box id="variableErrors" className={classes.variableErrors}>
      <Typography variant="h3" color="error">
        The following entries have errors:
      </Typography>
      {errorFields.map((error: string, index: number) => {
        return editError === error ? (
          <form
            key={index}
            onSubmit={(e: any) => {
              e.preventDefault();
              editSubmit(error, tempNewErrorState[index]);
            }}
          >
            <FormControl fullWidth>
              <TextField
                className={classes.editField}
                fullWidth
                id="modifyTextField"
                variant="outlined"
                onChange={(e: any) => {
                  const newErrorValues = errorFields.map((field: string) =>
                    field === error
                      ? e.target.value.toUpperCase()
                      : field.toUpperCase(),
                  );

                  setTempNewErrorState(newErrorValues);
                }}
                defaultValue={error}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        id="saveEdit"
                        type="submit"
                        title="Save Edit"
                        size="large"
                      >
                        <CheckIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </form>
        ) : (
          <Box key={index}>
            <Grid
              container
              id={`error-${error}`}
              className={classes.fieldErrors}
            >
              <Grid item xs={8} className="fieldName">
                <Typography color="error">{error}</Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  title="Delete Item"
                  id={`delete-${error}`}
                  onClick={() => removeErrorField(error)}
                  className="modifyButton"
                  size="large"
                >
                  <ClearIcon color="primary" />
                </IconButton>
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  title="Edit Item"
                  id={`edit-${error}`}
                  className="modifyButton"
                  onClick={() => setEditError(error)}
                  size="large"
                >
                  <Edit color="primary" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      <Button
        fullWidth
        id="deleteAllErrors"
        variant="contained"
        color="secondary"
        onClick={() => removeAllErrorFields(errorFields)}
      >
        Delete Error Variables
      </Button>
    </Box>
  );
  const reportingPeriods = yearChoice(1994, lastYear, "06/30/");
  const introParagraph = (
    <>
      {appHeaderTitle("customDataDownload")}
      <Typography variant="body2" id="peer-group-description">
        The SOD Custom Data Download allows you to tailor your download to your
        needs. You can choose between the entire national file or specify a
        state or territory for each data period going back to 1994.
      </Typography>
      <Typography variant="body2">
        Please note that the variables have been organized into three groups -
        institution, branch/office, and bank holding company. Select the
        variables that are appropriate, then click the download button. For
        quick description of each variable, hover over any of the information
        icons. For further assistance, see{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>SOD Help</RouterLink>.
      </Typography>
      <Typography variant="body2" id="peer-group-feedback-link">
        For more information, download{" "}
        <Link
          href={`${AppUrl("API")}/SOD_Variables_and_Definitions.csv`}
          download
        >
          SOD Variables and Definitions.csv
        </Link>{" "}
      </Typography>
    </>
  );

  type CustomConfig = {
    id: string;
    label: string;
    savedState: { location: ["ALL"]; variable: [""]; year: "" };
    value: string;
    uniqueId: string;
  };

  const handleConfigChange = (event: SelectChangeEvent) => {
    setConfigName(event.target.value);
    setSelectedConfig(
      savedConfigs.find((e: KeyValue) => e.id === event.target.value),
    );
    if (event.target.value === "ALL") {
      setSelectedOptions(selectedStatesAlpha.map(state => state.value));
      setSelectedStateAndTerritories(
        selectedStatesAlpha.map(state => state.value),
      );
    } else {
      setSelectedStateAndTerritories(selectedOptions);
    }
  };

  React.useEffect(() => {
    if (selectedConfig) {
      updateFilter("stname", selectedConfig?.savedState.location[0]);
      setSelectedVariables(selectedConfig?.savedState.variable.flat());
      updateFilter("lastYear", selectedConfig?.savedState.year);
      setSelectedStateAndTerritories(
        selectedConfig?.savedState.location.flat(),
      );
      setEditId(selectedConfig.uniqueId);
      setTempSelected(selectedConfig.savedState.location.flat());
    } else if (editConfig) {
      updateFilter("stname", editConfig?.savedState.location[0]);
      setSelectedVariables(editConfig?.savedState.variable.flat());
      updateFilter("lastYear", editConfig?.savedState.year);

      setSelectedStateAndTerritories(editConfig?.savedState.location.flat());
    } else {
      updateFilter("stname", "");
      setSelectedVariables(mandatoryVariables);
      updateFilter("lastYear", lastYear);
    }
  }, [selectedConfig, lastYear, updateFilter, mandatoryVariables, editConfig]);
  const [downloadCount, setDownloadCount] = React.useState(0);
  const [download, setDownload] = React.useState(false);
  const [getData, setGetData] = React.useState(false);
  const year = appState.filters.lastYear ?? lastYear;

  React.useEffect(() => {
    if (download) {
      setDownload(false);
      let isMounted = true;
      const stateFilter = `STALPBR:(${selectedStates
        .flat()
        .map(state => `"${state}"`)
        .join(" OR ")}) AND`;
      (async () => {
        await Promise.resolve(
          fetchData(
            `${ApiBaseUrl()}/sod?filters=${stateFilter} YEAR:${year}&limit=0`,
          )
            .then(response => {
              if (isMounted) {
                response.totals.count > 10000 && setLoading(true);

                setDownloadCount(response.totals.count);
              }
            })
            .catch((error: Error) => {
              console.error(error);
            })
            .finally(() => {
              isMounted = false;
              setGetData(true);
            }),
        );
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.filters.stname,
    download,
    selectedStateAndTerritories,
    selectedStates,
    year,
  ]);
  React.useEffect(() => {
    if (getData) {
      const downloadLimits = new Array(Math.floor(downloadCount / 10000))
        .fill(10000)
        .concat(downloadCount % 10000);

      let isMounted = true;
      const requests = downloadLimits.map((limit: number, int: number) => {
        return fetchData(
          `${ApiBaseUrl()}/sod?filters=STALPBR:${
            selectedStates.flat().length === selectedStatesAlpha.flat().length
              ? "*"
              : `(${selectedStates
                  .flat()
                  .map(state => `"${state}"`)
                  .join(" OR ")})`
          } AND YEAR:${year}&fields=${selectedVariables.join(
            ",",
          )}&limit=${limit}&offset=${10000 * int}`,
        );
      });
      setGetData(false);
      (async () => {
        await Promise.all(requests)
          .then(response => {
            if (isMounted) {
              const combinedData: KeyValue[] = response
                .map(res => res.data)
                .flat();
              setCustomDownloadData(sortObjValue(combinedData, "CERT"));
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            (anchorRef as any).current.link.click();
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.filters.stname,
    checkedVariables,
    getData,
    downloadCount,
    selectedVariables,
    selectedStateAndTerritories,
    selectedStates,
    year,
  ]);
  const initialCheckedNodes = React.useMemo(() => {
    const mandatory = data.find(node => node.id === "mandatory");
    return new Set(mandatory ? getAllNodeIds(mandatory) : []);
  }, []);
  const [checkedNodes, setCheckedNodes] = React.useState(initialCheckedNodes);
  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(
    new Set(),
  );

  React.useEffect(() => {
    setCheckedNodes(new Set(selectedVariables));
  }, [selectedVariables]);

  const handleToggle = (nodeId: string, checked: boolean) => {
    setCheckedNodes(prevCheckedNodes => {
      const newCheckedNodes = new Set(prevCheckedNodes);
      const toggleAllChildren = (nodes: TreeNode) => {
        if (checked) {
          newCheckedNodes.add(nodes.id);
        } else {
          newCheckedNodes.delete(nodes.id);
        }
        nodes.children?.forEach(child => toggleAllChildren(child));
      };

      for (const root of data) {
        if (nodeId === root.id) {
          toggleAllChildren(root);
        } else {
          if (checked) {
            newCheckedNodes.add(nodeId);
          } else {
            newCheckedNodes.delete(nodeId);
          }
          if (getNodeState(root, newCheckedNodes).checked) {
            newCheckedNodes.add(root.id);
          } else {
            newCheckedNodes.delete(root.id);
          }
        }
      }
      const checkedVariables = data.flatMap(root =>
        getCheckedVariables(root, newCheckedNodes),
      );
      setSelectedVariables(checkedVariables);
      setCheckedVariables(checkedVariables);
      return newCheckedNodes;
    });
  };
  const handleExpand = (nodeId: string) => {
    setExpandedNodes(prevExpandedNodes => {
      const newExpandedNodes = new Set(prevExpandedNodes);
      if (newExpandedNodes.has(nodeId)) {
        newExpandedNodes.delete(nodeId);
      } else {
        newExpandedNodes.add(nodeId);
      }
      return newExpandedNodes;
    });
  };
  const [openSaveModal, setOpenSaveModal] = React.useState(false);
  const [openRenameModal, setRenameModal] = React.useState(false);

  const [openLocationsModal, setOpenLocationsModal] = React.useState(false);
  const duplicateNameCheck =
    configName !== editConfig?.label &&
    savedConfigs.some(
      (e: KeyValue) => e.label === configName.trim().replace(/ /g, "_"),
    );
  const invalidCharactersCheck = /[^a-zA-Z0-9_ ]/g.test(configName);
  const modalInputField = (
    <SaveNameInput
      width={!createEditState ? "large" : "medium"}
      value={configName}
      onChange={setConfigName}
      header={
        selectedConfig?.id && !openSaveModal
          ? "Do you wish to continue?"
          : "Name this Configuration"
      }
      errors={duplicateNameCheck || invalidCharactersCheck}
      errorText={
        duplicateNameCheck
          ? "Configuration already exists"
          : invalidCharactersCheck
          ? "Configuration names may only contain alphanumeric characters. Special characters are not allowed."
          : ""
      }
      displayTextField={!!selectedConfig?.id && !openSaveModal}
    />
  );
  const [configResponse, setConfigResponse] = React.useState<KeyValue[]>([]);
  const matchingObject: any =
    configResponse.length > 0
      ? configResponse.find(item => item.name === sodState.name)
      : {};
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [newConfigCreated, setNewConfigCreated] = React.useState(false);
  const [editId, setEditId] = React.useState("");

  React.useEffect(() => {
    if (uuid.uuid || initialLoad || newConfigCreated) {
      setInitialLoad(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/custom_sod/my_custom_sod`,
          {},
          {
            Authorization: `Bearer ${getCookiebyName("id_token")}`,
          },
        )
          .then(response => {
            setSavedConfigs(
              response.data.map((e: KeyValue) => {
                return {
                  label: e.name,
                  value: e.short_name,
                  savedState: e.saved_state,
                  id: e.short_name,
                  uniqueId: e.ID,
                };
              }),
            );
            setConfigResponse(sortObjValue(response.data, "name"));
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => setNewConfigCreated(false));
      })();
    }
  }, [appState, initialLoad, newConfigCreated, uuid]);
  const configModal = (
    <ModalDialog
      title="Save Download Configuration"
      subtitle="Save your variable and geographic selections for quick and easy use in the future."
      display={openSaveModal}
      onExited={() => {}}
      setDisplay={setOpenSaveModal}
      maxWidth="md"
      noCloseXIcon
      onSubmit={() => {
        (async () => {
          await fetch(
            `${ApiBaseUrl()}/custom_sod`,
            apiPost(
              {
                title: configName,
                name: configName,
                short_name: configName.replace(/[^a-zA-Z0-9]/g, ""),
                ID: `${getCookiebyName("uuid")}_${Date.now()}`,
                saved_state: {
                  year: [appState.filters.lastYear],
                  variable: [selectedVariables],
                  location: [...selectedStateAndTerritories],
                },

                created_by: getCookiebyName("uuid"),
              },
              { Authorization: `Bearer ${getCookiebyName("id_token")}` },
            ),
          ).catch(error => console.error(error));
          setNewConfigCreated(true);
          setOpenSaveModal(false);
        })();
      }}
      submitText="Save"
      disabledSubmit={
        !configName || duplicateNameCheck || invalidCharactersCheck
      }
    >
      {modalInputField}
    </ModalDialog>
  );
  const configModal2 = (
    <ModalDialog
      title="Overwrite Download Configuration?"
      subtitle="You are about to overwrite a previously saved SOD Download Configuration.
      This cannot be undone. To continue, you can overwrite the original or rename it,
      which will create a new SOD Download Configuration while keeping the original intact."
      display={openRenameModal}
      onExited={() => {}}
      setDisplay={setRenameModal}
      maxWidth="md"
      noCloseXIcon
      onSubmit={() => {
        (async () => {
          fetch(
            `${ApiBaseUrl()}/custom_sod/`,
            apiPost(
              {
                title: configName,
                name: configName,
                short_name: configName.replace(/[^a-zA-Z0-9]/g, ""),
                ID: editId,
                saved_state: {
                  year: [appState.filters.lastYear],
                  variable: [selectedVariables],
                  location: [...selectedStateAndTerritories],
                },

                created_by: getCookiebyName("uuid"),
              },
              { Authorization: `Bearer ${getCookiebyName("id_token")}` },
            ),
          ).catch(error => console.error(error));
          setNewConfigCreated(true);
          setRenameModal(false);
        })();
      }}
      submitText="Yes, overwrite it."
      disabledSubmit={
        (!configName || duplicateNameCheck || invalidCharactersCheck) &&
        selectedConfig?.id === ""
      }
      overwrite={"No, rename it."}
      handleClick={() => {
        setOpenSaveModal(true);
        setRenameModal(false);
      }}
    >
      {modalInputField}
    </ModalDialog>
  );
  const customDownloadTreeView = (
    <Grid spacing={2} container>
      <Grid item sm={8}>
        <Typography
          variant={"h3"}
          style={{ marginTop: createEditState ? 0 : 16 }}
        >
          Select the variables to include in your custom download
        </Typography>
        <TreeView
          defaultCollapseIcon={<ExpandLessIcon style={{ fontSize: 30 }} />}
          defaultExpandIcon={<ExpandMoreIcon style={{ fontSize: 30 }} />}
          sx={{ flexGrow: 1, maxWidth: 1000, overflowY: "auto" }}
        >
          {data.map(root =>
            renderTree(
              root,
              checkedNodes,
              expandedNodes,
              handleToggle,
              handleExpand,
              root.id === "mandatory" ||
                (selectedConfig &&
                  arraysAreEqual(
                    selectedVariables.flat(),
                    selectedConfig.savedState.variable.flat(),
                  )),
            ),
          )}
        </TreeView>
      </Grid>
      <Grid item sm={4}>
        {!createEditState && (
          <>
            <Typography variant={"h3"} style={{ marginTop: 16 }}>
              Selected Locations
            </Typography>
            <Typography
              id="list-states"
              style={{ marginBottom: "1rem", wordBreak: "break-all" }}
            >
              {selectedStateAndTerritories.length === selectedStatesAlpha.length
                ? "All States and Territories"
                : selectedStateAndTerritories.join(",")}
            </Typography>{" "}
          </>
        )}
        <Typography variant={"h3"} style={{ marginTop: 16 }}>
          Mandatory Variables
        </Typography>
        <Typography
          id="list-var"
          style={
            selectedVariables.length > 0
              ? { marginBottom: "1rem", wordBreak: "break-all" }
              : {}
          }
        >
          {mandatoryVariables.join(",")}
        </Typography>
        {!editMany && (
          <Typography variant={"h3"} style={{ marginTop: 16 }}>
            Selected Variables
          </Typography>
        )}
        {editMany ? (
          editManyDisplay
        ) : (
          <>
            <Button
              fullWidth
              id="manuallyEdit"
              variant="contained"
              color="secondary"
              onClick={() => {
                setEditMany(true);
                setSelectedValues(
                  selectedVariables
                    .filter(e => {
                      return !mandatoryVariables.includes(e.trim());
                    })
                    .join(`\n`)
                    .toUpperCase(),
                );
              }}
              disabled={false}
            >
              Manually Edit
            </Button>
            {errorFieldDisplay}
            {!createEditState && (
              <>
                <Grid container spacing={2}>
                  {getCookiebyName("uuid") && (
                    <Grid item sm={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          selectedConfig?.id
                            ? setRenameModal(true)
                            : setOpenSaveModal(true);
                        }}
                        disabled={false}
                      >
                        Save Configuration
                      </Button>
                    </Grid>
                  )}
                  {selectedConfig?.id && !openSaveModal
                    ? configModal2
                    : configModal}
                  <Grid item sm={getCookiebyName("uuid") ? 6 : 12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => setDownload(true)}
                    >
                      Download
                      {loading && (
                        <Box
                          sx={{
                            display: "flex",
                            position: "absolute",
                            right: 30,
                          }}
                        >
                          <CircularProgress size={20} color="secondary" />
                        </Box>
                      )}
                    </Button>
                    <CSVLink
                      ref={anchorRef}
                      style={{ display: "none" }}
                      headers={selectedVariables.map(item => ({
                        label: item,
                        key: item,
                      }))}
                      filename={`${SODCustomfileNameFormat(
                        `SOD_CustomDownload_${
                          appState.filters.stname
                            ? transformStateName(appState.filters.stname)
                            : "ALL"
                        }_${appState.filters.lastYear}`,
                      )}.csv`}
                      data={customDownloadData}
                      asyncOnClick={true}
                    />
                  </Grid>
                </Grid>
                <Typography
                  id="list-var"
                  style={
                    selectedVariables.length > 0
                      ? { marginBottom: "1rem", wordBreak: "break-all" }
                      : {}
                  }
                >
                  {selectedVariables
                    .filter(e => {
                      return !mandatoryVariables.includes(e.trim());
                    })
                    .join(",")}
                </Typography>
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );

  const container = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Custom Data Download" />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        createEditState={createEditState}
        introText={introParagraph}
        backButton={
          <BackButton app="SOD" buttonLabel="Back to SOD" variant="dark" />
        }
      >
        {!createEditState && (
          <Box style={{ backgroundColor: "#1A4480" }}>
            (
            <Grid container>
              {getCookiebyName("uuid") && (
                <Grid item xs={4} padding={2}>
                  <List className={classes.list}>
                    <Typography
                      variant="body2"
                      className={classes.searchPanelHeadings}
                    >
                      Select Configuration
                    </Typography>
                    <SelectFilterField
                      currentValue={configName}
                      fieldName={""}
                      options={[
                        {
                          label: "Use Interface Selections Below",
                          value: "",
                        },
                        ...savedConfigs,
                      ]}
                      onChange={handleConfigChange}
                    />
                  </List>
                </Grid>
              )}
              <Grid item xs={getCookiebyName("uuid") ? 4 : 8} padding={2}>
                <List className={classes.list}>
                  <Typography
                    variant="body2"
                    className={classes.searchPanelHeadings}
                  >
                    Data for Year Ending
                  </Typography>
                  <SelectFilterField
                    currentValue={appState.filters.lastYear}
                    fieldName="lastYear"
                    options={reportingPeriods}
                    form={customForm}
                    setForm={setCustomForm}
                  />
                </List>
              </Grid>
              <Grid item xs={4} padding={2}>
                <List className={classes.list}>
                  <Typography
                    variant="body2"
                    className={classes.searchPanelHeadings}
                  >
                    Geographic Scope
                  </Typography>

                  <ModalDialog
                    title=""
                    subtitle="Filter results with these selections."
                    display={openLocationsModal}
                    onExited={() =>
                      setTempSelected(selectedStateAndTerritories.flat())
                    }
                    setDisplay={setOpenLocationsModal}
                    maxWidth="lg"
                    noCloseXIcon
                    moveActionButtons
                    onSubmit={() => {
                      setSelectedStateAndTerritories(tempSelected);
                      setOpenLocationsModal(false);
                    }}
                    submitText="Save"
                    disabledSubmit={
                      selectedStateAndTerritories.length < 1 ||
                      selectedStateAndTerritories.flat().length < 1
                    }
                  >
                    <GeographicScopeSelection />
                  </ModalDialog>

                  <Button
                    onClick={() => setOpenLocationsModal(true)}
                    style={{
                      backgroundColor: "#ffffff",
                      width: 423,
                      height: 40,
                      marginTop: 8,
                      fontSize: 16,
                    }}
                  >
                    Select Locations
                  </Button>
                </List>
              </Grid>
            </Grid>
            )
          </Box>
        )}
        {createEditState && (
          <Box className={classes.dialogActions}>
            <Grid container>
              <Grid item xs={8} padding={2}>
                <Typography
                  style={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "32px",
                  }}
                >
                  {editState
                    ? "Edit an SOD Custom Download"
                    : "Create an SOD Custom Download"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Button
                  onClick={() => {
                    history.push({
                      pathname: `${AppUrl("PeerGroup")}/manage`,
                      state: {
                        url: AppUrl("SOD") + window.location.search,
                      },
                    });
                  }}
                  color="secondary"
                  variant="contained"
                  id="cancel-button"
                  sx={{ width: "35%" }}
                >
                  Cancel
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  id="save-button"
                  sx={{ width: "35%" }}
                  disabled={
                    !configName || duplicateNameCheck || invalidCharactersCheck
                  }
                  onClick={() => {
                    if (!editState) {
                      (async () => {
                        fetch(
                          `${ApiBaseUrl()}/custom_sod`,
                          apiPost(
                            {
                              title: configName,
                              name: configName,
                              short_name: configName.replace(
                                /[^a-zA-Z0-9]/g,
                                "",
                              ),
                              saved_state: {
                                ...(createEditState
                                  ? {}
                                  : { year: [appState.filters.lastYear] }),
                                variable: [selectedVariables],
                                location: [...selectedStateAndTerritories],
                              },

                              created_by: getCookiebyName("uuid"),
                            },
                            {
                              Authorization: `Bearer ${getCookiebyName(
                                "id_token",
                              )}`,
                            },
                          ),
                        ).catch(error => console.error(error));
                        setNewConfigCreated(true);
                        setOpenSaveModal(false);
                      })();
                    } else {
                      const postFetch = (endpoint: string, post: KeyValue) => {
                        fetch(`${ApiBaseUrl()}/${endpoint}`, post);
                      };

                      const savedState = {
                        year: matchingObject.saved_state.year,
                        variable: selectedVariables,
                        location: [...selectedStateAndTerritories],
                      };
                      const value = {
                        ...matchingObject,
                        title: configName,
                        name: configName,
                        short_name: configName,
                        saved_state: savedState,
                      };
                      const user = getCookiebyName("id_token");
                      const renamePostReq = apiPost(
                        { ...value },
                        { "x-auth": "bfs", "Authorization": `Bearer ${user}` },
                      );
                      postFetch("custom_sod", renamePostReq);
                    }
                    history.push({
                      pathname: `${AppUrl("PeerGroup")}/manage`,
                      reload: `reloadConfigs`,
                      state: {
                        url: AppUrl("SOD") + window.location.search,
                      },
                    });
                  }}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {createEditState && (
          <Grid container>
            <Grid item xs={5} paddingTop={2} paddingLeft={1}>
              <Typography variant="body2">{modalInputField}</Typography>
            </Grid>
            <Grid item xs={3} padding={2} paddingLeft={0}>
              <Typography variant={"h3"}>Geographic Scope</Typography>
              <ModalDialog
                title=""
                subtitle="Filter results with these selections."
                display={openLocationsModal}
                setDisplay={setOpenLocationsModal}
                maxWidth="lg"
                noCloseXIcon
                moveActionButtons
                onSubmit={() => {
                  setSelectedStateAndTerritories(tempSelected);
                  setOpenLocationsModal(false);
                }}
                onExited={() => {
                  setTempSelected(selectedStateAndTerritories.flat());
                }}
                submitText="Save"
                disabledSubmit={
                  !!selectedConfig || selectedStateAndTerritories.length < 1
                }
              >
                <GeographicScopeSelection />
              </ModalDialog>

              <Button
                onClick={() => setOpenLocationsModal(true)}
                color="secondary"
                variant="contained"
                id="select-locations-button"
                fullWidth
              >
                Select Locations
              </Button>
            </Grid>
            <Grid item xs={4} padding={2}>
              {createEditState && (
                <>
                  <Typography variant={"h3"}>Selected Locations</Typography>
                  <Typography
                    id="list-states"
                    style={{ marginBottom: "1rem", wordBreak: "break-all" }}
                  >
                    {selectedStateAndTerritories.length ===
                    selectedStatesAlpha.length
                      ? "All States and Territories"
                      : selectedStateAndTerritories.join(",")}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </AppBody>
      {customDownloadTreeView}
    </>
  );
  return container;
}
